import { forwardRef, useContext } from 'react'
import { type Components, Virtuoso } from 'react-virtuoso'
import FluentChevronDown20Filled from '~icons/fluent/chevron-down-20-filled'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import { useCommentsRedesignExperiment } from 'src/experiments/experiments/2605/commentsRedesign'
import { cx } from 'src/utils/cx'
import { shouldNotHappen } from 'src/utils/error'
import { CommentItem } from './CommentItem'
import { CommentContext, DEFAULT_LOAD_SIZE, ensureContext } from './context'

/**
 * A comment list
 *
 * Should be used within a `<CommentProvider>`.
 */
export function CommentList(
  props: StyleProps & {
    /**
     * Whether to use scroll-to-load mode.
     *
     * @default "button"
     */
    loadMode?: 'button' | 'scroll'
    /**
     * Whether to use window-rendering mode, basically a flag to use Virtuoso.
     * Note that when using window-rendering mode, a height must be set.
     * Otherwise, Virtuoso renders nothing.
     */
    windowed?: boolean
  },
) {
  const { messages, loadMore } = useContext(CommentContext) ?? ensureContext()
  const { isTreatment } = useCommentsRedesignExperiment()
  const context: VirtuosoContext = {
    loadMode: props.loadMode ?? 'button',
    treatment: isTreatment,
  }

  return props.windowed ? (
    <Virtuoso
      data={messages}
      context={context}
      components={virtuosoComponents}
      itemContent={(_, message) => <CommentItem message={message} />}
      className={props.className}
      style={props.style}
      endReached={() => {
        props.loadMode === 'scroll' && loadMore(DEFAULT_LOAD_SIZE)
      }}
    />
  ) : (
    <div>
      <virtuosoComponents.List context={context} data-testid="">
        {messages.map(message => (
          <CommentItem key={message.id} message={message} />
        ))}
      </virtuosoComponents.List>
      <virtuosoComponents.Footer context={context} />
    </div>
  )
}

type VirtuosoContext = {
  loadMode: 'button' | 'scroll'
  treatment: boolean
}

const virtuosoComponents = {
  List: forwardRef(function VirtuosoList({ context, ...props }, ref) {
    return (
      <div
        ref={ref}
        {...props}
        className={cx('flex flex-col', context?.treatment ? 'gap-6' : 'gap-4')}
      />
    )
  }),

  Footer: ({ context }) => {
    const { messages, havingMore, everHadMore, loading, loadMore } =
      useContext(CommentContext) ?? ensureContext()
    const { loadMode, treatment } = context ?? shouldNotHappen()

    // Hide "reached the end" when the initial load contained everything.
    if (
      treatment &&
      !loading &&
      !havingMore &&
      messages.length > 0 &&
      !everHadMore
    ) {
      return null
    }

    const emptyLabel = treatment ? (
      <Text
        i18nKey="comment:be-first-to-comment"
        defaults="Be the first to comment"
      />
    ) : (
      <Text i18nKey="comment:leave-comments-here" />
    )

    const label = loading ? (
      <Text i18nKey="common:hint.loading" />
    ) : havingMore ? (
      <Text i18nKey="action:load-more" />
    ) : messages.length ? (
      <Text i18nKey="common:list-reached-the-end" />
    ) : (
      emptyLabel
    )

    if (treatment) {
      const isEmpty = !loading && !havingMore && messages.length === 0
      return (
        <div
          className={cx(
            'flex items-center gap-3 text-sm',
            messages.length > 0 && 'mt-5',
          )}
        >
          <span className="flex-1 h-px bg-black/10 dark:bg-white/10" />
          {isEmpty ? (
            <span className="text-neutral-500">{label}</span>
          ) : (
            <UIButton
              variant="ghost"
              size="small"
              busy={loading}
              disabled={!havingMore}
              icon={
                havingMore ? (
                  <FluentChevronDown20Filled className="size-4" />
                ) : null
              }
              className={cx(
                'px-3 py-1.5 gap-1 rounded-full',
                'border border-black/10 dark:border-white/15',
                'text-neutral-600 dark:text-neutral-400',
                'hover:bg-black/5 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-neutral-100',
                !havingMore && 'border-dashed',
              )}
              onPress={() => {
                loadMode === 'button' && loadMore(DEFAULT_LOAD_SIZE)
              }}
            >
              {label}
            </UIButton>
          )}
          <span className="flex-1 h-px bg-black/10 dark:bg-white/10" />
        </div>
      )
    }

    return (
      <div className={cx('pl-14 text-sm', messages.length > 0 && 'mt-3')}>
        <UIButton
          variant="ghost"
          size="small"
          busy={loading}
          disabled={!havingMore}
          icon={
            havingMore ? <FluentChevronDown20Filled className="size-5" /> : null
          }
          className={cx(
            'pl-1 -ml-2 pr-2 py-1 gap-1',
            !havingMore && 'text-neutral-500',
          )}
          onPress={() => {
            loadMode === 'button' && loadMore(DEFAULT_LOAD_SIZE)
          }}
        >
          {label}
        </UIButton>
      </div>
    )
  },
} satisfies Components<never, VirtuosoContext>
